import Alert from "components/common/Alert";
import { validateOrganizer } from "utils/companyValidation";
import { useState, useEffect } from "react";
import { getImageUrl } from "utils/imageHelper";
import { Country, State, City } from "country-state-city";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import ImageViewer from "components/common/ImageViewer";
import React from "react";
import Select from "react-select";



export default function OrganizerForm({
  onSubmit,
  initialData = null,
  submitLabel = "Create Organizer",
  alert,
  setAlert,
  onBack,
}) {
  const [existingLogo, setExistingLogo] = useState(null);
  const [errors, setErrors] = useState({});
  const [emailInput, setEmailInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false); 

  const alertRef = React.useRef(null);
  

  const [formData, setFormData] = useState({
    companyName: "",
    companyEmail: "",
    companyLogo: null,
    companyMobileCountryCode: "+91",
    companyMobile: "",
    companyPhoneNumber: "",
    companyAddress: "",
    companyCountry: "",
    companyState: "",
    companyDistrict: "",
    companyCity: "",
    companyPincode: "",
    companyGST: "",
    companyWebsite: "",

    adminName: "",
    adminEmail: "",
    adminPassword: "",
    adminMobileCountryCode: "+91",
    adminMobile: "",

    communicationEmails: [],
    projectModules: [],
  });

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      height: "48px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#c7d2fe" : "#d1d5db",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#a5b4fc",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "48px",
      padding: "0 12px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "48px",
    }),
  };

  useEffect(() => {
  if (!initialData) return;

  let emails = initialData.communicationEmails || [];

  if (
    emails.length === 1 &&
    typeof emails[0] === "string" &&
    emails[0].includes(",")
  ) {
    emails = emails[0].split(",").map(e => e.trim());
  }

  setFormData((prev) => ({
    ...prev,
    ...initialData,
    companyLogo: null,
    communicationEmails: emails,
    projectModules: initialData.projectModules || [],
  }));

  if (initialData.companyLogo) {
    setExistingLogo(initialData.companyLogo);
  }
}, [initialData]);


  React.useEffect(() => {
  if (alert && alertRef.current) {
    alertRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
}, [alert]);

  const countryOptions = Country.getAllCountries();
  const stateOptions = formData.companyCountry
    ? State.getStatesOfCountry(formData.companyCountry)
    : [];
  const districtOptions =
    formData.companyCountry && formData.companyState
      ? City.getCitiesOfState(
          formData.companyCountry,
          formData.companyState
        )
      : [];

  const countryCodeOptions = countryOptions.map((c) => ({
  value: `+${c.phonecode}`,
  label: `${c.flag} +${c.phonecode} (${c.name})`,
  }));

  const countrySelectOptions = countryOptions.map((c) => ({
    value: c.isoCode,
    label: c.name,
  }));

  const stateSelectOptions = stateOptions.map((s) => ({
  value: s.isoCode,
  label: s.name,
  }));

  const districtSelectOptions = districtOptions.map((d) => ({
  value: d.name,
  label: d.name,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleFileChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const allowedTypes = ["image/jpeg", "image/png"];
  if (!allowedTypes.includes(file.type)) {
    setErrors((p) => ({
      ...p,
      companyLogo: "Only JPEG and PNG files are allowed",
    }));
    return;
  }

  const img = new Image();
  img.src = URL.createObjectURL(file);

  img.onload = () => {
    const REQUIRED_WIDTH = 120;
    const REQUIRED_HEIGHT = 40;

    // if (img.width !== REQUIRED_WIDTH || img.height !== REQUIRED_HEIGHT) {
    //   setErrors((p) => ({
    //     ...p,
    //     companyLogo: `Logo must be exactly ${REQUIRED_WIDTH} × ${REQUIRED_HEIGHT} px`,
    //   }));
    //   return;
    // }

    setFormData((p) => ({ ...p, companyLogo: file }));
    setExistingLogo(null);
    setErrors((p) => ({ ...p, companyLogo: "" }));
  };
};


  const handleModuleChange = (module) => {
    setFormData((p) => ({
      ...p,
      projectModules: p.projectModules.includes(module)
        ? p.projectModules.filter((m) => m !== module)
        : [...p.projectModules, module],
    }));
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors((p) => ({
        ...p,
        communicationEmails: "Invalid email format",
      }));
      return;
    }

    if (formData.communicationEmails.includes(email)) {
      setErrors((p) => ({
        ...p,
        communicationEmails: "Email already added",
      }));
      return;
    }

    setFormData((p) => ({
      ...p,
      communicationEmails: [...p.communicationEmails, email],
    }));

    setEmailInput("");
    setErrors((p) => ({ ...p, communicationEmails: "" }));
  };

  const removeEmail = (index) => {
    setFormData((p) => ({
      ...p,
      communicationEmails: p.communicationEmails.filter((_, i) => i !== index),
    }));
  };

  const updateEmail = (index, value) => {
    setFormData((p) => ({
      ...p,
      communicationEmails: p.communicationEmails.map((e, i) =>
        i === index ? value : e
      ),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateOrganizer(
      formData,
      Boolean(initialData)
    );

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setAlert("Please fix the highlighted errors");
      return;
    }

    setErrors({});
    setAlert("");
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-10">
      {alert && (
        <div
          ref={alertRef}
          className="px-6 pt-3 scroll-mt-[100px]"
        >
          <Alert
            type="error"
            message={alert}
            onClose={() => setAlert("")}
          />
        </div>
      )}

      <Section title="Organizer Details">
        <Input label="Organizer Name" name="companyName" value={formData.companyName} onChange={handleChange} error={errors.companyName} />
        <Input label="Organizer Email" name="companyEmail" value={formData.companyEmail} onChange={handleChange} error={errors.companyEmail} />
        <div>
            <label className="mb-1 block text-sm font-medium">
              Organizer Mobile Country Code<span className="text-red-500">*</span>
            </label> 
            <Select
              options={countryCodeOptions}
              value={countryCodeOptions.find(
                (o) => o.value === formData.companyMobileCountryCode
              )}
              onChange={(opt) =>
                setFormData((p) => ({
                  ...p,
                  companyMobileCountryCode: opt.value,
                }))
              }
              placeholder="Search by +91, 91, India..."
              isSearchable
              classNamePrefix="react-select"
              styles={selectStyles}
            />
            {errors.companyMobileCountryCode && (
                          <p className="text-xs text-red-500">{errors.companyMobileCountryCode}</p>
                        )}
          </div>
        <Input label="Organizer Mobile" name="companyMobile" value={formData.companyMobile} onChange={handleChange} error={errors.companyMobile} required/>
        <Input label="Phone Number" name="companyPhoneNumber" value={formData.companyPhoneNumber} onChange={handleChange} error={errors.companyPhoneNumber} required/>
        <Input label="Website" name="companyWebsite" value={formData.companyWebsite} onChange={handleChange} error={errors.companyWebsite}/>
        <Input label="GST Number" name="companyGST" value={formData.companyGST} onChange={handleChange} error={errors.companyGST} />
        <Input label="Zipcode" name="companyPincode" value={formData.companyPincode} onChange={handleChange} error={errors.companyPincode} />
        <div>
        <label className="mb-1 block text-sm font-medium">
          Country<span className="text-red-500">*</span>
        </label>
        <Select
          options={countrySelectOptions}
          value={countrySelectOptions.find(
            (o) => o.value === formData.companyCountry
          )}
          onChange={(opt) =>
            setFormData((p) => ({
              ...p,
              companyCountry: opt.value,
              companyState: "",
              companyCity: "",
              companyDistrict: "",
            }))
          }
          placeholder="Search country..."
          isSearchable
          classNamePrefix="react-select"
          styles={selectStyles}
        />

        {errors.companyCountry && (
          <p className="text-xs text-red-500">{errors.companyCountry}</p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">
          State<span className="text-red-500">*</span>
        </label>
        <Select
          options={stateSelectOptions}
          value={stateSelectOptions.find(
            (o) => o.value === formData.companyState
          )}
          onChange={(opt) =>
            setFormData((p) => ({
              ...p,
              companyState: opt.value,
              companyCity: "",
              companyDistrict: "",
            }))
          }
          isDisabled={!formData.companyCountry}
          placeholder="Search state..."
          isSearchable
          classNamePrefix="react-select"
          styles={selectStyles}
        />

        {errors.companyState && (
          <p className="text-xs text-red-500">{errors.companyState}</p>
        )}
      </div>


      <div>
        <label className="mb-1 block text-sm font-medium">
          District
        </label>

        <Select
          options={districtSelectOptions}
          value={districtSelectOptions.find(
            (o) => o.value === formData.companyDistrict
          )}
          onChange={(opt) =>
            setFormData((p) => ({
              ...p,
              companyDistrict: opt.value,
              companyCity: opt.value,
            }))
          }
          isDisabled={!formData.companyState}
          placeholder="Search district..."
          isSearchable
          classNamePrefix="react-select"
          styles={selectStyles}
        />


        {errors.companyDistrict && (
          <p className="text-xs text-red-500">
            {errors.companyDistrict}
          </p>
        )}
      </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Organizer Logo<span className="text-red-500">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      (Recommended resolution: <b>120 × 40 px</b>. Accepted formats: JPEG, PNG. )
                  </span>
                  </label>

                  

                  {existingLogo && (
                    <>
                    <img
                      src={getImageUrl(existingLogo)}
                      alt="Organizer Logo"
                      className="mb-3 h-24 rounded-lg border object-contain"
                      onClick={() => setViewerOpen(true)} 
                    />
                    <ImageViewer
                          open={viewerOpen}
                          onClose={() => setViewerOpen(false)}
                          src={getImageUrl(existingLogo)}
                        />
                        </>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full rounded-xl border p-3"
                  />

                  {errors.companyLogo && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.companyLogo}
                    </p>
                  )}
                </div>
                <div>
                <label className="mb-1 block text-sm font-medium">
                    Organizer Address
                  </label>
                <textarea
                    name="companyAddress"
                    placeholder="Address"
                    value={formData.companyAddress}
                    onChange={handleChange}
                    className="w-full rounded-xl border p-3"
                  />
                  </div>
              </Section>

      <Section title="Admin Details">
        <Input label="Admin Name" name="adminName" value={formData.adminName} onChange={handleChange} error={errors.adminName} />
        <Input label="Admin Email" name="adminEmail" value={formData.adminEmail} onChange={handleChange} error={errors.adminEmail} />
        <div>
        <label className="mb-1 block text-sm font-medium">
          Admin Mobile Country Code<span className="text-red-500">*</span>
        </label> 
                <Select
                  options={countryCodeOptions}
                  value={countryCodeOptions.find(
                    (o) => o.value === formData.adminMobileCountryCode
                  )}
                  onChange={(opt) =>
                    setFormData((p) => ({
                      ...p,
                      adminMobileCountryCode: opt.value,
                    }))
                  }
                  placeholder="Search by +91, India..."
                  isSearchable
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
 
        {errors.adminMobileCountryCode && (
          <p className="text-xs text-red-500">{errors.adminMobileCountryCode}</p>
        )}
      </div>

        <Input label="Admin Mobile" name="adminMobile" value={formData.adminMobile} onChange={handleChange} error={errors.adminMobile}  required/>

        
        <div>
          <label className="mb-1 block text-sm font-medium">
            Password<span className="text-red-500">*</span>
          </label>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="adminPassword"
              value={formData.adminPassword}
              onChange={handleChange}
              className={`w-full rounded-xl border p-3 pr-10 ${
                errors.adminPassword ? "border-red-400" : "border-gray-300"
              }`}
              placeholder="Enter password"
            />

            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {showPassword ? (
                <MdVisibility size={20} />
              ) : (
                <MdVisibilityOff size={20} />
              )}
            </button>
          </div>

          {errors.adminPassword && (
            <p className="mt-1 text-xs text-red-500">
              {errors.adminPassword}
            </p>
          )}
        </div>


      </Section>

       <Section title="Project Modules">
          {["HR", "CRM", "Inventory"].map((m) => (
            <Checkbox
              key={m}
              label={m}
              checked={formData.projectModules.includes(m)}
              onChange={() => handleModuleChange(m)}
            />
          ))}

          {errors.projectModules && (
            <p className="text-xs text-red-500 col-span-2">
              {errors.projectModules}
            </p>
          )}
        </Section>

      <Section title="Communication Emails">
        <div className="flex gap-2 col-span-2">
          <input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Enter email & press Add"
            className="flex-1 rounded-xl border p-3"
          />
          <button type="button" onClick={addEmail} className="rounded-xl bg-brand-500 px-4 text-white">
            Add
          </button>
        </div>

        <div className="col-span-2 flex flex-wrap gap-2">
          {formData.communicationEmails.map((email, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
              <input
                value={email}
                onChange={(e) => updateEmail(i, e.target.value)}
                className="bg-transparent outline-none"
              />
              <button type="button" onClick={() => removeEmail(i)} className="text-red-500">
                ✕
              </button>
            </div>
          ))}
        </div>

        {errors.communicationEmails && (
          <p className="text-xs text-red-500 col-span-2">{errors.communicationEmails}</p>
        )}
      </Section>

      {/* SUBMIT */}
      <div className="flex justify-between">
        {onBack && (
          <button type="button" onClick={onBack} className="rounded-xl bg-red-500 px-6 py-3 text-white"
          >
            Back
          </button>
        )}
        <button type="submit" className="rounded-xl bg-brand-500 px-6 py-3 text-white">
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </section>
  );
}

function Input({ label, error, required = false, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label} {required && <span className="text-red-500 ml-1">*</span>}</label>
      <input
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-400" : "border-gray-300"
        }`}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Checkbox({ label, ...props }) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" {...props} />
      {label}
    </label>
  );
}

